import React, { useEffect, useState } from 'react';
import { Form, FloatingLabel } from 'react-bootstrap';
import RichTextEditor from '../RichTextEditor';

interface ContentFormProps {
    image: File[]; // Array of images
    image_name?: [] | null;
    album_image: File | null; // Single album image
    warningMessage?: string;
    album_image_name?: string;
    post_date: string; // Post date
    event_date_start: string; // Event date start
    event_date_end: string; // Event date end
    isPermanent: boolean; // Is permanent
    category: string; // Category
    folder_name: string; // Folder name
    album_name: string; // Album name
    album_code: string; // Album code
    slug: string; // Slug
    onImageChange: (e: React.ChangeEvent<HTMLInputElement>) => void; // Handler for image changes
    onAlbumImageChange: (e: React.ChangeEvent<HTMLInputElement>) => void; // Handler for album image change
    onPostDateChange: (e: React.ChangeEvent<HTMLInputElement>) => void; // Handler for post date change
    onFolderNameChange: (e: React.ChangeEvent<HTMLInputElement>) => void; // Handler for folder name change
    onAlbumNameChange: (e: React.ChangeEvent<HTMLInputElement>) => void; // Handler for album name change
    onAlbumCodeChange: (e: React.ChangeEvent<HTMLInputElement>) => void; // Handler for album code change
    onSlugChange: (e: React.ChangeEvent<HTMLInputElement>) => void; // Handler for slug change
    onCategoryChange: (e: React.ChangeEvent<HTMLSelectElement>) => void; // Handler for category change
    onEventDateStartChange: (e: React.ChangeEvent<HTMLInputElement>) => void; // Handler for event date start change
    onEventDateEndChange: (e: React.ChangeEvent<HTMLInputElement>) => void; // Handler for event date end change
    onIsPermanentChange: (e: React.ChangeEvent<HTMLInputElement>) => void; // Handler for is permanent change
}

export default function ContentForm({
    image_name,
    album_image_name,
    post_date,
    event_date_end,
    event_date_start,
    isPermanent,
    category,
    folder_name,
    warningMessage,
    album_name,
    album_code,
    slug,
    onImageChange,
    onAlbumImageChange,
    onPostDateChange,
    onEventDateEndChange,
    onEventDateStartChange,
    onIsPermanentChange,
    onCategoryChange,
    onAlbumNameChange,
    onAlbumCodeChange,
    onSlugChange,
}: ContentFormProps) {


    return (
        <>
            <Form.Group className="mb-3 pb-3" controlId="formImage">
                <Form.Label className="text-sm mb-2">Images</Form.Label>
                <Form.Control type="file" multiple onChange={onImageChange} />
                {warningMessage && (
                    <div style={{ color: 'red', marginBottom: '10px' }}>
                        {warningMessage}
                    </div>
                )}
                {(image_name) && (
                    <Form.Text className="italic text-black pt-3 d-block hover:text-red-500">Number of uploaded images on the server</Form.Text>)}
                <Form.Text className="italic text-black d-block hover:text-red-500">{image_name ? `${image_name.length} images on server` : 'No image uploaded'}</Form.Text>

            </Form.Group>

            {/* Album Image Input */}
            <Form.Group className="mb-3" controlId="formAlbumImage">
                <Form.Label className="text-sm mb-2">Album Image</Form.Label>
                <Form.Control type="file" onChange={onAlbumImageChange} />
                {(album_image_name) && (
                    <Form.Text className="italic text-black d-block pt-3 hover:text-red-500">Uploaded Album Image</Form.Text>)}
                <Form.Text className="italic text-black d-block pb-3 hover:text-red-500">{album_image_name ? <a href={`https://cdn.pulp.ph/storage/${album_image_name}`} target="_blank" rel="noopener noreferrer">{album_image_name}</a> : 'No Uploaded files on server'}</Form.Text>
            </Form.Group>

            {/* Category */}
            <Form.Group className="mb-3" controlId="formCategory">
                <label className='text-sm mb-2'>Category</label>
                <Form.Select aria-label="Select Category" className='bg-gray-200'
                    value={category} onChange={onCategoryChange}>
                    <option value=""> Select Ticket Group Code</option>
                    <option value="MeetAndGreet">Meet And Greet</option>
                    <option value="Shows">Shows</option>
                </Form.Select>
            </Form.Group>

            {/* Post Date */}
            <Form.Group className="mb-3" controlId="formPostDate">
                <label className="text-sm mb-2">Post Date</label>
                <FloatingLabel controlId="floatingInput" label="Post Date" className="mb-3">
                    <Form.Control
                        type="date"
                        placeholder="Post Date"
                        value={post_date ? new Date(post_date).toISOString().slice(0, 10) : ""}
                        className="bg-gray-200"
                        onChange={onPostDateChange}
                    />
                </FloatingLabel>
            </Form.Group>

            {/* Start Date */}
            <Form.Group className="mb-3" controlId="formStartDate">
                <label className="text-sm mb-2">Start Date</label>
                <FloatingLabel controlId="floatingInput" label="Start Date" className="mb-3">
                    <Form.Control
                        type="date"
                        placeholder="Start Date"
                        value={event_date_start ? new Date(event_date_start).toISOString().slice(0, 10) : ""}
                        className="bg-gray-200"
                        onChange={onEventDateStartChange}
                    />
                </FloatingLabel>
            </Form.Group>

            {/* End Date */}
            <Form.Group className="mb-3" controlId="formEndDate">
                <label className="text-sm mb-2">End Date</label>
                <FloatingLabel controlId="floatingInput" label="End Date" className="mb-3">
                    <Form.Control
                        type="date"
                        placeholder="End Date"
                        value={event_date_end ? new Date(event_date_end).toISOString().slice(0, 10) : ""}
                        className="bg-gray-200"
                        onChange={onEventDateEndChange}
                    />
                </FloatingLabel>
            </Form.Group>

            {/* Folder Name */}
            <div className="mb-3">
                <label className="text-sm mb-2">Folder Name:  <b>{folder_name}</b></label>
            </div>

            {/* Album Name */}
            <Form.Group className="mb-3" controlId="formAlbumName">
                <label className="text-sm mb-2">Album Name</label>
                <FloatingLabel controlId="floatingInput" label="Album Name" className="mb-3">
                    <Form.Control
                        type="text"
                        placeholder="Album Name"
                        value={album_name}
                        className="bg-gray-200"
                        onChange={onAlbumNameChange}
                    />
                </FloatingLabel>
            </Form.Group>

            {/* Album Code */}
            <Form.Group className="mb-3" controlId="formAlbumCode">
                <label className="text-sm mb-2">Album Code</label>
                <FloatingLabel controlId="floatingInput" label="Album Code" className="mb-3">
                    <Form.Control
                        type="text"
                        placeholder="Album Code"
                        value={album_code}
                        className="bg-gray-200"
                        onChange={onAlbumCodeChange}
                    />
                </FloatingLabel>
            </Form.Group>

            <Form.Group className="mb-3" controlId="formSlug">
                <label className="text-sm mb-2">Slug</label>
                <Form.Control
                    type="text"
                    placeholder="slug"
                    value={slug}
                    className="bg-gray-200"
                    onChange={onSlugChange}
                />
            </Form.Group>

            {/* Is Permanent */}
            <Form.Group className="mb-3" controlId="formisPermanent">
                <Form.Check
                    type="checkbox"
                    label="Temporary"
                    checked={isPermanent ? isPermanent : true}
                    onChange={onIsPermanentChange}
                />
            </Form.Group>
        </>
    );
}
